import { essentialFormApiConnector } from 'apiConnector/form/essential';
import { tabRemoved } from 'epic/workspace/common/tabs/tabsEpics';
import { ofType } from 'redux-observable';
import type { SchedulerLike } from 'rxjs';
import { of } from 'rxjs';
import { catchError, endWith, filter, map, mergeMap, takeUntil, timeoutWith, withLatestFrom } from 'rxjs/operators';
import { defaultFormReceivedThunk } from 'state/essential/thunks';
import { commonFormActions } from 'state/forms/actions/form';
import type { EpicWithThunk } from 'state/state.common';
import { workspaceTabCloneCompleted } from 'state/workspaces/workspace.actions';

export function cloneEssentialFormEpic(timeoutInMillis: number = 60000, appScheduler?: SchedulerLike): EpicWithThunk {
  return (action$, state$, { httpClient }) =>
    action$.pipe(
      ofType('WORKSPACE_TAB_CLONED'),
      filter(action => action.workspace === 'essential'),
      withLatestFrom(state$),
      mergeMap(
        ([
          { oldTabId, newTabId, clonedNegotiationId, workspace, tabType, productType, productSubtype: productSubType },
        ]) => {
          const tabRemoved$ = tabRemoved(action$, newTabId, state$);

          return essentialFormApiConnector(httpClient)
            .cloneForm({ newTabId, clonedNegotiationId, productType, productSubType })
            .pipe(
              takeUntil(tabRemoved$),
              map(
                ({
                  productView: newProductView,
                  blocksMetadata,
                  fieldsMetadata,
                  values,
                  version,
                  negotiationId,
                  actions,
                  quoteQuery,
                  errors,
                  mustForceAdvancedMode,
                  sendWholeFormData,
                  allowedRequests,
                  productLayout,
                }) =>
                  defaultFormReceivedThunk(
                    newTabId,
                    newProductView,
                    blocksMetadata,
                    fieldsMetadata,
                    values,
                    version,
                    negotiationId,
                    actions,
                    quoteQuery,
                    mustForceAdvancedMode,
                    allowedRequests,
                    errors,
                    sendWholeFormData,
                    productLayout,
                  ),
              ),
              catchError(error =>
                of(
                  commonFormActions.defaultFormFailed(
                    'essential',
                    newTabId,
                    error.message || 'clone failed',
                    error.traceId,
                  ),
                ),
              ),
              timeoutWith(
                timeoutInMillis,
                of(commonFormActions.defaultFormTimeouted('essential', newTabId)),
                appScheduler,
              ),
              endWith(workspaceTabCloneCompleted(oldTabId, workspace, tabType)),
            );
        },
      ),
    );
}
